<template>
	<div :class="{ 'info-panel': !mini, mini, ['max-per-row-' + maxPerRow]: true, alwaysEven: !!randomFrom && randomFrom % 2 === 0 }">
		<LoadingSpinner v-if="!collabInfoLoaded" />

		<div class="types" v-if="collabInfoLoaded">
			<div v-for="(type, index) in typesProcessed" :key="'type-' + index" class="type" @click="selectCollab(type.slug)">
				<div :style="{ 'background-image': 'url(' + type.image + ')' }" class="image"></div>
				<span>{{ type.title }}</span>
			</div>
		</div>

		<div class="no-results" v-if="collabInfoLoaded && typesProcessed.length === 0">Sorry we have no ideas that match your filters.</div>

		<a
			v-if="showTCs"
			href="https://partneruphub.com/wp-content/uploads/2024/04/PartnerUp-Brand-x-Brand-collab-guide-TsCs.pdf"
			target="_blank"
			class="tc"
			>Collab guide Ts & Cs.</a
		>
	</div>
</template>

<script>
import { computed, onMounted, getCurrentInstance } from "vue";
import LoadingSpinner from "./../LoadingSpinner.vue";

export default {
	name: "CollabTypes",
	components: { LoadingSpinner },
	props: {
		showTCs: {
			type: Boolean,
			default: true,
		},
		filterByEffort: {
			type: Array,
			default: () => [],
		},
		filterByBenefits: {
			type: Array,
			default: () => [],
		},
		filterByIds: {
			type: Array,
			default: () => [],
		},
		filterByTitles: {
			type: Array,
			default: () => [],
		},
		mini: {
			type: Boolean,
			default: false,
		},
		maxPerRow: {
			type: Number,
			default: 4,
		},
		randomFrom: {
			type: Number,
			default: 0,
		}
	},
	setup(props) {
		const vm = getCurrentInstance().proxy;
		const store = vm.$store;

		const collabInfo = computed(() => store.state.general.collabInfo);
		const collabInfoLoaded = computed(() => store.state.general.collabInfoLoaded);

		const normalizeTitle = (value) =>
			String(value || "")
				.toLowerCase()
				.replace(/\s+/g, " ")
				.trim();
		const filterIdSet = computed(() => props.filterByIds.map((id) => String(id)));
		const filterTitleSet = computed(() => props.filterByTitles.map((title) => normalizeTitle(title)));

		const typesProcessed = computed(() => {
			if (!collabInfoLoaded.value) return [];
			let types = [];
			const idFilters = filterIdSet.value;
			const titleFilters = filterTitleSet.value;
			const hasIdFilters = idFilters.length > 0;
			const hasTitleFilters = titleFilters.length > 0;

			collabInfo.value.list.forEach((type) => {
				if (props.filterByEffort.length > 0 && props.filterByEffort.indexOf(type.effort) < 0) return;
				if (props.filterByBenefits.length > 0 && !type.benefits.some((benefit) => props.filterByBenefits.includes(benefit))) return;

				if (hasIdFilters || hasTitleFilters) {
					const idMatch = hasIdFilters && idFilters.includes(String(type.id));
					const titleMatch = hasTitleFilters && titleFilters.includes(normalizeTitle(type.title));
					if (!idMatch && !titleMatch) return;
				}

				types.push(type);
			});
			if (props.randomFrom > 0) {
				return types.sort(() => Math.random() - 0.5).slice(0, props.randomFrom);
			}
			return types;
		});

		const updateVar = (payload) => store.commit("general/updateVar", payload);
		const loadCollabIdeas = () => store.dispatch("general/loadCollabIdeas");

		const selectCollab = (type) => {
			updateVar({ varName: "selectedInspiration", info: type });
		};

		onMounted(() => {
			loadCollabIdeas();
		});

		return {
			collabInfoLoaded,
			typesProcessed,
			selectCollab,
		};
	},
};
</script>
<style lang="less" scoped>
@import "./../../assets/css/less/_settings.less";
@import "./../../assets/css/less/_helper.less";

.tc {
	color: @brown;
	display: inline-block;
	margin: 20px 0 0;
}

.no-results {
	text-align: center;
	margin: 20px 0;
}

.types {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 25px;
	align-items: stretch;
	padding: 0;

	.type {
		background-color: #333;
		.rounded(20px);
		cursor: pointer;
		border: none;
		.trans;
		overflow: hidden;
		position: relative;
		width: auto;
		height: 100%;
		margin: 0;
		min-height: 0;
		padding: 0;
		aspect-ratio: 1 / 0.5;
		display: flex;
		align-items: flex-end;

		.image {
			width: 100%;
			height: 100%;
			position: absolute;
			left: 0;
			top: 0;
			background-size: cover;
			background-position: center center;
			opacity: 0.5;
			.trans;
		}

		&::after {
			content: "";
			position: absolute;
			bottom: 0;
			left: 0;
			right: 0;
			height: 55%;
			background: linear-gradient(to top, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0) 100%);
			z-index: 1;
		}

		span {
			color: white;
			position: relative;
			z-index: 2;
			font-size: 1.4rem;
			font-weight: 400;
			padding: 14px 16px;
			display: block;
			text-align: left;
			text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
		}

		&:hover {
			.image {
				opacity: 0.85;
				transform: scale(1.03);
			}
		}
	}
}

.max-per-row-2 {
	.types {
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 10px;
		.type {
			aspect-ratio: 1 / 0.35;
		}
	}
}

.mini {
	.types {
		.type {
			span {
				font-size: 1.1rem;
			}
		}
	}
}

@media screen and (max-width: 1023px) {
	.types {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}
	&.alwaysEven {
		.types {
			grid-template-columns: repeat(4, minmax(0, 1fr));
		}
	}
	&.max-per-row-2 {
		.types {
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: 10px;
		}
	}
}

@media screen and (max-width: 700px) {
	&.alwaysEven {
		.types {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
}

@media screen and (max-width: 640px) {
	.types {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
	&.alwaysEven {
		.types {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
	.max-per-row-2 {
		.types {
			.type {
				aspect-ratio: 1 / 0.65;
			}
		}
	}
}

@media screen and (max-width: 480px) {
	.types {
		grid-template-columns: repeat(1, minmax(0, 1fr));
	}
}
</style>